import { useState } from 'react';
import {
  Page,
  Card,
  BlockStack,
  InlineStack,
  Text,
  TextField,
  Select,
  Button,
  Banner,
  IndexTable,
} from '@shopify/polaris';
import { useResource } from '../../../shared/hooks/useResource';
import { useResourceMutation } from '../../../shared/hooks/useResourceMutation';
import { ErrorBanner } from '../../../shared/components/ErrorBanner';
import { LoadingState } from '../../../shared/components/LoadingState';
import {
  ShippingSettings,
  getShippingSettings,
  updateShippingSettings,
  createBracket,
  deleteBracket,
} from './api';

const DEFAULT_SHOP_DOMAIN = 'fjord-ice-baths-2.myshopify.com';

const MODE_OPTIONS = [
  { label: 'Manual rates (TNT stood down)', value: 'manual' },
  { label: 'TNT live rates', value: 'tnt' },
];

const RATE_TYPE_OPTIONS = [
  { label: 'Flat rate per order', value: 'flat' },
  { label: 'Per item', value: 'per_item' },
  { label: 'Weight based', value: 'weight' },
];

function dollars(minorUnits: number): string {
  return (minorUnits / 100).toFixed(2);
}

function toMinorUnits(value: string): number {
  return Math.round(Number(value || '0') * 100);
}

interface SettingsFormProps {
  shopDomain: string;
  settings: ShippingSettings;
}

function SettingsForm({ shopDomain, settings }: SettingsFormProps): JSX.Element {
  const queryKey = [['shipping-settings', shopDomain]];

  const [mode, setMode] = useState<string>(settings.mode);
  const [freeShippingTags, setFreeShippingTags] = useState(settings.freeShippingTags);
  const [freeServiceName, setFreeServiceName] = useState(settings.freeServiceName);
  const [freeWeightThresholdKg, setFreeWeightThresholdKg] = useState(
    String(settings.freeWeightThresholdG / 1000),
  );
  const [rateType, setRateType] = useState<string>(settings.consumableRateType);
  const [consumableServiceName, setConsumableServiceName] = useState(
    settings.consumableServiceName,
  );
  const [flatPrice, setFlatPrice] = useState(dollars(settings.flatPriceMinorUnits));
  const [perItemBase, setPerItemBase] = useState(dollars(settings.perItemBaseMinorUnits));
  const [perItemPrice, setPerItemPrice] = useState(dollars(settings.perItemPriceMinorUnits));

  const [bracketMinKg, setBracketMinKg] = useState('0');
  const [bracketMaxKg, setBracketMaxKg] = useState('5');
  const [bracketPrice, setBracketPrice] = useState('15.00');

  const saveMutation = useResourceMutation(updateShippingSettings, queryKey);
  const addBracketMutation = useResourceMutation(createBracket, queryKey);
  const deleteBracketMutation = useResourceMutation(deleteBracket, queryKey);

  function handleSave(): void {
    saveMutation.mutate({
      shopDomain,
      mode: mode as 'manual' | 'tnt',
      freeShippingTags,
      freeServiceName,
      freeWeightThresholdG: Math.round(Number(freeWeightThresholdKg || '50') * 1000),
      consumableRateType: rateType as 'flat' | 'per_item' | 'weight',
      consumableServiceName,
      flatPriceMinorUnits: toMinorUnits(flatPrice),
      perItemBaseMinorUnits: toMinorUnits(perItemBase),
      perItemPriceMinorUnits: toMinorUnits(perItemPrice),
    });
  }

  function handleAddBracket(): void {
    addBracketMutation.mutate({
      shopDomain,
      minWeightG: Math.round(Number(bracketMinKg || '0') * 1000),
      maxWeightG: Math.round(Number(bracketMaxKg || '0') * 1000),
      priceMinorUnits: toMinorUnits(bracketPrice),
    });
  }

  return (
    <BlockStack gap="400">
      {saveMutation.error ? <ErrorBanner error={saveMutation.error} /> : null}
      {addBracketMutation.error ? <ErrorBanner error={addBracketMutation.error} /> : null}
      {deleteBracketMutation.error ? <ErrorBanner error={deleteBracketMutation.error} /> : null}

      <Card>
        <BlockStack gap="300">
          <Text as="h2" variant="headingMd">
            Rate mode
          </Text>
          <Select label="Mode" options={MODE_OPTIONS} value={mode} onChange={setMode} />
          {mode === 'tnt' ? (
            <Banner tone="warning">
              TNT live rates are currently stood down. Switching back on requires valid TNT
              credentials and the registered carrier service.
            </Banner>
          ) : null}
        </BlockStack>
      </Card>

      <Card>
        <BlockStack gap="300">
          <Text as="h2" variant="headingMd">
            Free shipping (ice baths &amp; showers)
          </Text>
          <Text as="p" variant="bodyMd" tone="subdued">
            Products carrying any of these tags are priced inclusive of shipping and return a $0
            rate at checkout.
          </Text>
          <TextField
            label="Free shipping product tags (comma separated)"
            value={freeShippingTags}
            onChange={setFreeShippingTags}
            autoComplete="off"
            helpText="Matches Shopify product tags, e.g. ice-bath, ice-shower"
          />
          <TextField
            label="Rate name shown at checkout"
            value={freeServiceName}
            onChange={setFreeServiceName}
            autoComplete="off"
          />
          <TextField
            label="Fallback weight threshold (kg)"
            type="number"
            value={freeWeightThresholdKg}
            onChange={setFreeWeightThresholdKg}
            autoComplete="off"
            helpText="Safety net if the tag lookup is unavailable: items at or above this weight ship free."
          />
        </BlockStack>
      </Card>

      <Card>
        <BlockStack gap="300">
          <Text as="h2" variant="headingMd">
            Consumables shipping
          </Text>
          <Select
            label="Pricing strategy"
            options={RATE_TYPE_OPTIONS}
            value={rateType}
            onChange={setRateType}
          />
          <TextField
            label="Rate name shown at checkout"
            value={consumableServiceName}
            onChange={setConsumableServiceName}
            autoComplete="off"
          />

          {rateType === 'flat' ? (
            <TextField
              label="Flat rate ($)"
              type="number"
              value={flatPrice}
              onChange={setFlatPrice}
              autoComplete="off"
              prefix="$"
            />
          ) : null}

          {rateType === 'per_item' ? (
            <InlineStack gap="300">
              <TextField
                label="Base charge ($)"
                type="number"
                value={perItemBase}
                onChange={setPerItemBase}
                autoComplete="off"
                prefix="$"
              />
              <TextField
                label="Per item ($)"
                type="number"
                value={perItemPrice}
                onChange={setPerItemPrice}
                autoComplete="off"
                prefix="$"
              />
            </InlineStack>
          ) : null}

          {rateType === 'weight' ? (
            <BlockStack gap="300">
              <IndexTable
                resourceName={{ singular: 'bracket', plural: 'brackets' }}
                itemCount={settings.weightBrackets.length}
                selectable={false}
                headings={[
                  { title: 'From (kg)' },
                  { title: 'To (kg)' },
                  { title: 'Price' },
                  { title: '' },
                ]}
              >
                {settings.weightBrackets.map((bracket, index) => (
                  <IndexTable.Row id={bracket.id} key={bracket.id} position={index}>
                    <IndexTable.Cell>{bracket.minWeightG / 1000}</IndexTable.Cell>
                    <IndexTable.Cell>{bracket.maxWeightG / 1000}</IndexTable.Cell>
                    <IndexTable.Cell>${dollars(bracket.priceMinorUnits)}</IndexTable.Cell>
                    <IndexTable.Cell>
                      <Button
                        tone="critical"
                        variant="plain"
                        loading={deleteBracketMutation.isPending}
                        onClick={() => deleteBracketMutation.mutate(bracket.id)}
                      >
                        Delete
                      </Button>
                    </IndexTable.Cell>
                  </IndexTable.Row>
                ))}
              </IndexTable>
              <InlineStack gap="300" blockAlign="end">
                <TextField
                  label="From (kg)"
                  type="number"
                  value={bracketMinKg}
                  onChange={setBracketMinKg}
                  autoComplete="off"
                />
                <TextField
                  label="To (kg)"
                  type="number"
                  value={bracketMaxKg}
                  onChange={setBracketMaxKg}
                  autoComplete="off"
                />
                <TextField
                  label="Price ($)"
                  type="number"
                  value={bracketPrice}
                  onChange={setBracketPrice}
                  autoComplete="off"
                  prefix="$"
                />
                <Button onClick={handleAddBracket} loading={addBracketMutation.isPending}>
                  Add bracket
                </Button>
              </InlineStack>
              <Text as="p" variant="bodySm" tone="subdued">
                Orders heavier than the top bracket are charged the top bracket price. Weight mode
                with no brackets returns no rates and blocks checkout — always keep at least one.
              </Text>
            </BlockStack>
          ) : null}
        </BlockStack>
      </Card>

      <InlineStack align="end">
        <Button variant="primary" onClick={handleSave} loading={saveMutation.isPending}>
          Save settings
        </Button>
      </InlineStack>
    </BlockStack>
  );
}

export function ShippingRatesPage(): JSX.Element {
  const [shopDomainInput, setShopDomainInput] = useState(DEFAULT_SHOP_DOMAIN);
  const [activeDomain, setActiveDomain] = useState(DEFAULT_SHOP_DOMAIN);

  const query = useResource(['shipping-settings', activeDomain], (api) =>
    getShippingSettings(api, activeDomain),
  );

  return (
    <Page
      title="Shipping rates"
      subtitle="Manual rates: baths & showers ship free, consumables are configurable"
    >
      <BlockStack gap="400">
        <Card>
          <InlineStack gap="300" blockAlign="end">
            <TextField
              label="Shop domain"
              value={shopDomainInput}
              onChange={setShopDomainInput}
              autoComplete="off"
            />
            <Button onClick={() => setActiveDomain(shopDomainInput.trim())}>Load</Button>
          </InlineStack>
        </Card>

        {query.error ? <ErrorBanner error={query.error} /> : null}
        {query.isLoading ? <LoadingState message="Loading settings..." /> : null}
        {query.data ? (
          <SettingsForm
            key={`${activeDomain}:${query.data.id}`}
            shopDomain={activeDomain}
            settings={query.data}
          />
        ) : null}
      </BlockStack>
    </Page>
  );
}
